// No raw key in localStorage. Non-extractable WebCrypto key and encrypted payloads
// are stored in IndexedDB. Storage failure stops capture; no plaintext fallback.
const request = (r) => new Promise((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error); });
const complete = (t) => new Promise((resolve, reject) => { t.oncomplete = resolve; t.onerror = t.onabort = () => reject(t.error || new Error('Protected storage unavailable')); });
let opening;
async function database() {
  if (!window.isSecureContext || !crypto.subtle || !window.indexedDB) throw new Error('Protected storage unavailable. Capture is paused.');
  if (!opening) opening = new Promise((resolve, reject) => {
    const r = indexedDB.open('dutilyne-private-captures', 1);
    r.onupgradeneeded = () => { r.result.createObjectStore('keys'); r.result.createObjectStore('captures', { keyPath: 'id' }); };
    r.onsuccess = () => resolve(r.result); r.onerror = () => { opening = null; reject(r.error); };
  });
  return opening;
}
async function keyFor(uid) {
  const db = await database();
  const old = await request(db.transaction('keys').objectStore('keys').get(uid));
  if (old) return old;
  const fresh = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  const t = db.transaction('keys', 'readwrite'); const done = complete(t);
  const again = t.objectStore('keys').get(uid);
  again.onsuccess = () => { if (!again.result) t.objectStore('keys').put(fresh, uid); };
  await done;
  return request(db.transaction('keys').objectStore('keys').get(uid));
}
async function seal(key, bytes) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  return { iv, data: await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, bytes) };
}
async function unseal(key, encrypted) {
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv: encrypted.iv }, key, encrypted.data);
}
export async function loadCaptures(uid) {
  const db = await database(), key = await keyFor(uid);
  const rows = await request(db.transaction('captures').objectStore('captures').getAll());
  return Promise.all(rows.filter(r => r.uid === uid).map(async r => ({
    ...JSON.parse(new TextDecoder().decode(await unseal(key, r.meta))),
    photo: r.photo ? new Blob([await unseal(key, r.photo)], { type: 'image/jpeg' }) : null,
  })));
}
export async function saveCapture(uid, entry) {
  const db = await database(), key = await keyFor(uid);
  const all = await request(db.transaction('captures').objectStore('captures').getAll());
  const { photo, ...meta } = entry;
  const total = all.filter(r => r.id !== uid+':'+entry.event_id).reduce((n, r) => n+(r.photo?.data?.byteLength || 0), 0);
  if (total+(photo?.size || 0) > 64*1024*1024 || all.length > 300) throw new Error('Protected capture storage is full. Deliver or dismiss pending captures first.');
  const row = { id: uid+':'+entry.event_id, uid, meta: await seal(key, new TextEncoder().encode(JSON.stringify(meta))),
    photo: photo ? await seal(key, await photo.arrayBuffer()) : null };
  const t = db.transaction('captures', 'readwrite'); const done = complete(t); t.objectStore('captures').put(row); await done;
}
export async function removeCapture(uid, eid) {
  const db = await database(), t = db.transaction('captures', 'readwrite'), done = complete(t);
  t.objectStore('captures').delete(uid+':'+eid); await done;
}
export async function prepareCaptureStorage(uid) {
  const key = await keyFor(uid);
  const value = await seal(key, new Uint8Array([1, 2, 3])); await unseal(key, value);
  return loadCaptures(uid);
}